<script setup>
import CheckBox from "@/components/common/input/CheckBox";
import MovingTooltip from "@/components/common/other/MovingTooltip";
import Button from "@/components/common/input/Button";
import StaticTooltip from "@/components/common/other/StaticTooltip";

import {ref, computed, onMounted} from 'vue';
import {useStore} from "vuex";

const store = useStore();

const props = defineProps({

  columns: Array,
  dataCy: String,

  currentPage: {type: Number, default: 1},
  currentPaginationRange: {type: Number},
  pageSize: {type: Number, default: 100},
  allowGridMenu: {type: Boolean, default: false},
  allowPagination: {type: Boolean, default: true},
  removeHighlightedRow: {type: Boolean, default: false},
  selectable: {type: Boolean, default: true},
  rowSelection: {type: Boolean, default: true},
  height: {type: String, default: '500px'},

  dataSource: {
    type: Array,
    default: function () {
      return []
    }
  },

})

const emits = defineEmits(['update:currentPaginationRange', 'update:currentPage', 'click-reset',
  'click-save', 'selected-row'],)

const sourceColumnIndex = ref(null);
const localData = ref([]);
const openColumnMenuByIndex = ref(null);
const pageButtons = ref([]);
const showGridMenu = ref(false);
const closeTimer = ref(null);

const paginationSize = ref(10);

const customColumns = computed(() => {
  return props.columns.filter(c => c.enabled === true)
});

const pagedData = computed(() => {

  if (localData.value.length > 0) {

    return localData.value.filter((row, index) => {

      // eg if page size set to 10, and current page is 2 (default)
      // currentPage is a v-model to the chosen state class

      let startIndex = (props.currentPage - 1) * props.pageSize; // 1 X 10 = 10   / start = 10
      let endIndex = props.currentPage * props.pageSize; // 2 X 10 = 20   / end = 20

      // if the current index >= 10 (start), and the index is less then 20 (end), return the array from index 10 to 19  = 10 records inclusive (page size!)
      if (index >= startIndex && index < endIndex) {
        return true
      }

    });
  }
})

onMounted(() => {

  localData.value = JSON.parse(JSON.stringify(props.dataSource));

  createPageButtons();
})

function extendLastColumn(columns, index) {
  let lastColumn = columns.length - 1;

  if (index === lastColumn) {

    let width = parseInt(columns[lastColumn].width)
    let extendedWidth = width + 12;
    return extendedWidth.toString();
  }

  return columns[index].width

}

function rowData(row, key, column) {

  if (column) {

    // Get the row data from the past in key from the current column
    if (typeof (row[key]) === 'string') {

      if (row[key]) return row[key]

    }

  }

  return row[key];
}

function rowClick(e, row, index) {
  clearHighlightedRow();
  row.highlightSelectedRow = true;

  if (props.selectable && !row.checkRow) emits('selected-row', props.dataSource[index], index);

}

function clearHighlightedRow() {

  for (let ea in localData.value) {
    localData.value[ea]['highlightSelectedRow'] = false;
  }

}

function rowDataSource(index) {
  return props.dataSource[index];
}

function setCellColor(column, row) {
  // Return the css color value eg(#345321) held in the row data using the matching prop set in the column array
  if (column.cellColorProp) return row[column.cellColorProp];
}

function setRowColor(column, row) {
  if (column.rowColorProp) return row[column.rowColorProp];
}

function setFontColor(column, row) {
  // if (row[column.cellColorProp]) return '#fff'
}

function copyValue(text) {
  navigator.clipboard.writeText(text);
}

function handleColumnWidthSave() {
  if(props.allowGridMenu) emits('click-save', true);
}

function handleDragStart(e, column) {
  sourceColumnIndex.value = props.columns.findIndex(i => i.id === column.id);

  if (openColumnMenuByIndex.value !== null) {e.preventDefault();return};
  if(column.isFixed) {e.preventDefault(); return};

}

function handleDragover(e) {
  e.preventDefault();
  return false;
}

function handleDrop(e, destinationColumnData) {
  e.stopPropagation();

  let mainArrayDestinationIndex = props.columns.findIndex(i => i.id === destinationColumnData.id)

  if (sourceColumnIndex.value !== mainArrayDestinationIndex && !destinationColumnData.isFixed) {

    if (mainArrayDestinationIndex < sourceColumnIndex.value) {

      props.columns.splice(mainArrayDestinationIndex, 0, props.columns[sourceColumnIndex.value]);
      props.columns.splice(sourceColumnIndex.value + 1, 1);

    } else {
      props.columns.splice(mainArrayDestinationIndex + 1, 0, props.columns[sourceColumnIndex.value]);
      props.columns.splice(sourceColumnIndex.value, 1);

    }

    if(props.allowGridMenu) emits('click-save');

  }
}

function createPageButtons() {

  pageButtons.value = [];

  let roundUp = Math.ceil(localData.value.length / props.pageSize);

  for (let i = 0; i < roundUp; i++) {

    pageButtons.value.push(
        {
          number: i + 1,
          notValid: false
        });
  }

}

function getPaginationPages() {
  // Calc the number of pagination buttons required, eg blocks of pages set in paginationSize (10 default)
  return Math.ceil(pageButtons.value.length / paginationSize.value);

}

function splitPagination() {

  return pageButtons.value.filter((row, index) => {

    let startIndex = (props.currentPaginationRange - 1) * paginationSize.value;
    let endIndex = props.currentPaginationRange * paginationSize.value;

    if (index >= startIndex && index < endIndex) {
      return true
    }

  });

}

function pageUp() {
  if (props.currentPaginationRange < getPaginationPages())
    emits('update:currentPaginationRange', props.currentPaginationRange + 1)
}

function pageDown() {
  if (props.currentPaginationRange > 1) emits('update:currentPaginationRange', props.currentPaginationRange - 1)
}

function handleColumnMenu(e, index) {
  e.preventDefault();
  openColumnMenuByIndex.value ? openColumnMenuByIndex.value = null :   openColumnMenuByIndex.value = index;
}

function handleMenuDisplay() {
  showGridMenu.value = !showGridMenu.value;
}

function handleMouseEnter() {
  clearTimeout(closeTimer.value)
}

function handleMouseLeave() {
  closeTimer.value = setTimeout(() => showGridMenu.value = false, 600);
}

function resetColumns() {
  props.columns.forEach(function (item) {
    item.enabled = true;
  })
  props.columns.sort((a, b) => {
    return a.id - b.id
  });
}

function handleCloseColMenu() {
  openColumnMenuByIndex.value = null;

  if(props.allowGridMenu) handleColumnWidthSave();
}

function dragColumnsMessage(col) {
  if(!col.isFixed) return 'Drag Over Other Colum Headers To Re Order';
  return 'This Column Is Fixed';
}

</script>

<template class="cmp-data-grid">
  <div v-if="showGridMenu" class="cmp-data-grid_menu">
    <div class="cmp-data-grid_menu-title" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">&#9783 Column
      Configuration
      <div style="font-size: 10px; margin-left: 20px">Add / Remove Columns</div>
      <div class="cmp-data-grid_menu-button-area">
        <MovingTooltip>
          <template #content>Reset All Columns To Default</template>
          <Button
              @click="emits('click-reset', $event, resetColumns())"
              type="toolbar"
              text="Reset">
          </Button>
<!--          <i @click="emits('click-reset', $event, resetColumns())" class="fa-regular fa-clock-rotate-left"-->
<!--             style="color: #F68E1E; font-size: 19px"></i>-->
        </MovingTooltip>
        <MovingTooltip>
          <template #content>Save Column Selection</template>
          <Button
              @click="emits('click-save', $event)"
              text="Save">
          </Button>
<!--          <i @click="emits('click-save', $event)" class="fa-regular fa-floppy-disk" style="color: #3f9968"></i>-->
        </MovingTooltip>
      </div>
    </div>
    <div class="cmp-data-grid_menu-checkbox-area" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
      <div v-for="(column, index) in columns">
        <CheckBox
            v-if="column.isRemovable"
            v-model="column.enabled"
            :label-width="145"
            :label="column.title">
        </CheckBox>
      </div>
    </div>
  </div>
  <div class="cmp-data-grid_wrapper">
    <div class="cmp-data-grid_menu-area">
      <div class="cmp-data-grid_pagination-area">
        <div v-if="props.allowGridMenu" @click="handleMenuDisplay" class="cmp-data-grid_menu-icon">&#9776</div>
        <div v-if="getPaginationPages() > 0" class="cmp-data-grid_pages-area">{{ pageButtons.length }}</div>
        <div v-if="pageButtons.length > 1" style="margin-right: 10px;"> Pages:</div>
        <div v-if="pageButtons.length === 1" style="margin-right: 10px;"> Page</div>
        <div v-if="getPaginationPages() > 1" style="margin-left: 10px; margin-right: 20px;">
          <StaticTooltip :icon="false">
            <template #content>
              <span v-if="props.currentPaginationRange > 1">Prev Pages</span>
              <span v-else> No Previous Pages</span>
            </template>
            <Button :disabled="props.currentPaginationRange === 1" style="width: 20px;" @click="pageDown()"
                    type="toolbar" text="<"></Button>
          </StaticTooltip>
          <StaticTooltip :icon="false">
            <template #content>
              <span v-if="props.currentPaginationRange !== getPaginationPages()">Next Pages</span>
              <span v-else> No More Pages</span>
            </template>
            <Button :disabled="props.currentPaginationRange === getPaginationPages()" style="width: 20px;"
                    @click="pageUp()" type="toolbar" text=">"></Button>
          </StaticTooltip>
        </div>
        <div v-for="(button, index) in splitPagination()" :key="index">
          <Button
              @click="$emit('update:currentPage', button.number)"
              style="width: 30px;"
              :class="`${button.number === props.currentPage && 'cmp-data-grid_active-page'}`"
              type="toolbar"
              :text="button.number.toString()">
          </Button>
        </div>
      </div>
      <div class="cmp-data-grid_menu-area-slot-content">
        <slot name="menu-slot-items"></slot>
      </div>
    </div>
    <div class="cmp-data-grid_column-header"
         draggable="true"
         :key="col.id"
         :title="dragColumnsMessage(col)"
         v-for="(col, index) in customColumns"
         @dragstart="handleDragStart($event, col)"
         @dragover="handleDragover($event)"
         @drop="handleDrop($event, col)"
         @contextmenu="handleColumnMenu($event, index)"
         @click="clearHighlightedRow">
      <div :style="{width: extendLastColumn(customColumns, index) + 'px'}"
           class="cmp-data-grid_grid-cell cmp-data-grid_last-column">{{ col.title }}
        <!--<span @click="sortColumns(col)" class="cmp-data-grid_sort-column-arrows" v-if="setSortCondition(index, col)"> &#8645</span>-->
        <span v-if="col.key === 'selectedRow'" class="cmp-data-grid_select-all-rows">
          <slot name="selectAllRows"></slot></span>
      </div>
      <div v-if="index === openColumnMenuByIndex"
           class="cmp-data-grid_column-menu" title="Column Width" @mouseleave="handleCloseColMenu">
        <div style="color: #131313; padding: 5px;">
          <i style="color: #131313;" class="fa-solid fa-text-width"></i> Adjust Width
          <span style="margin-left: 65px" title="Save New Width">
<!--                <i @click="handleColumnWidthSave" v-if="props.allowGridMenu"-->
<!--                   style="color: #1b6222; font-size: 16px; cursor: pointer" class="fa-regular fa-floppy-disk">-->
<!--                </i>-->
          </span>
        </div>
        <input
            class="cmp-data-grid_column-slider"
            v-model="col.width"
            type="range"
            min="78"
            max="1000"/>
      </div>
    </div>
    <div v-if="localData.length === 0" class="cmp-data-grid_no-data"><i class="fa-solid fa-database"></i> No Data</div>
    <div v-if="localData.length > 0" class="cmp-data-grid_data-wrapper" :style="{maxHeight: props.height + 'px'}">
      <div class="cmp-data-grid_row" v-for="(row, index) in pagedData" :key="index"
           @click="rowClick($event, row, index)">
        <template v-for="(column, colIndex) in customColumns">
          <div v-if="typeof $slots[column.key] !== 'undefined'" :key="colIndex" @contextmenu="$event.preventDefault()"
               :style="{width: column.width + 'px', background: setCellColor(column, row)}"
               class="cmp-data-grid_grid-cell-slot"
               :class="{'cmp-data-grid_selected-row' : row.highlightSelectedRow === true && selectable,
                  'cmp-data-grid_highlight-row' : setRowColor(column, row) }">
            <slot :name="column.key" :column="column" :grid="row" :rowData="rowDataSource(index)" :rowIndex="index"></slot>
          </div>
          <div v-else @contextmenu="$event.preventDefault()"
               :class="{'cmp-data-grid_selected-row' : row.highlightSelectedRow === true  && selectable,
                 'cmp-data-grid_read-only' : column.readOnly && row.readOnly, 'cmp-data-grid_highlight-row' : setRowColor(column, row) }"
               :data-cy="dataCy + 'ColumnIndex' + colIndex"
               :style="{width: column.width + 'px', background: setCellColor(column, row), color: setFontColor(column, row)}"
               :key="index"
               class="cmp-data-grid_grid-cell">
            <StaticTooltip :icon="false" maxWidth="300">
              <template #content>
                {{ rowData(row, column.key) }}
              </template>
              <span
                  :class="{'cmp-data-grid_selected-row' : row.highlightSelectedRow === true  && selectable}"
                  :data-cy="dataCy +'RowIndex' + index"
                  @click="copyValue(rowData(row, column.key, column))">{{ rowData(row, column.key, column) }}
              </span>
            </StaticTooltip>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>

.cmp-data-grid {

  &_wrapper {
    position: relative;
    width: max-content;
    font-size: 12px;
    margin-top: 30px;
  }

  &_column-header {
    position: relative;
    display: inline-block;
    cursor: pointer;
  }

  &_column-header * {
    /*main-theme css variable set in app.vue*/
    background: var(--grid-header);
    color: var(--side-menu-text-color);
  }

  &_data-wrapper {
    width: 100%;
    overflow-y: scroll;
    overflow-x: hidden;
    overscroll-behavior-y: none;
    margin-top: -4px;
    border-bottom: 1px solid #d4d4d4;
  }

  &_row {
    display: flex;
    background-color: #ffffff;
    height: 33px;
  }

  &_row:nth-child(even) {
    background-color: #f6f6f6;
  }

  &_grid-cell, &_grid-cell-slot {
    position: relative;
    border: 1px solid #d4d4d4;
    float: left;
    padding: 4px 5px 5px 6px;
    margin-bottom: -1px;
    margin-right: -1px;
    display: grid;
    line-height: 2.0;
    align-items: center;
    overflow: hidden;
  }

  &_grid-cell span {
    cursor: copy;
  }

  &_last-column {
    height: 25px;
    z-index: 99;
  }

  &_selected-row {
    background: #f9efe0 !important;
    /*color: #0e0e0e;*/
  }

  &_highlight-row {
    background: #fff4f0;
  }

  &_read-only {
    color: #a0a0a0;
    background-color: #fcfcfc;
  }

  &_menu-icon {
    font-size: 17px;
    width: 20px;
    cursor: pointer;
    margin-right: 20px;
    margin-left: 15px;
    color: #101010;
    font-weight: bold;
  }

  &_menu-area {
    position: absolute;
    display: grid;
    display: flex;
    justify-content: space-between;
    top: -38px;
    width: -webkit-fill-available;
    width: -moz-available;
    padding: 5px;
    background-image: linear-gradient(90deg, #f5f5f5, #ffffff);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    min-width: 886px;
    height: 24px;
  }

  &_menu-area-slot-content {
    display: flex;
    align-items: center;
  }

  &_pagination-area {
    display: flex;
    align-items: center;
  }

  &_pages-area {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    padding: 8px;
    height: 22px;
    background-color: #ffffff;
    margin-right: 10px;
    font-size: 12px;
    color: #000000;
    border-radius: 4px;
  }

  &_menu {
    position: absolute;
    //top: 109px;
    z-index: 9999;
    width: 280px;
    border: 1px solid #dddddd;
    border-top: 0;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
    background-image: linear-gradient(0deg, #f6f6f6, #ffffff);
    box-shadow: 0px 1px 2px 0px #888888;
    //backdrop-filter: blur(24px);
  }

  &_menu-title {
    position: absolute;
    //margin-left: 6px;
    left: 40px;
    font-size: 14px;
    padding: 0 20px 20px 20px;
    height: 300px;
    width: 160px;
  }

  &_menu-button-area {
    margin-top: 15px;
    display: flex;
    gap: 30px;
    font-size: 20px;

  }

  &_menu-checkbox-area {
    height: 300px;
    overflow-y: auto;
    margin-top: 100px;
    padding-left: 30px;
  }

  &_active-page {
    background-image: none !important;
    background-color: white;
    font-weight: bold;
    color: black !important;
    box-shadow: 1px 1px 2px 0px #484848 !important;
  }

  &_column-menu {
    position: absolute;
    cursor: default;
    bottom: 34px;
    width: 220px;
    height: 46px;
    color: #131313;
    border: 1px solid var(--border-color);
    border-top-right-radius: var(--rounded-corners);
    border-top-left-radius: var(--rounded-corners);
    background-color: white !important;
    z-index: 999999;
  }

  &_column-menu * {
    background: none;
  }

  &_column-slider {
    -webkit-appearance: none;
    width: 200px;
    height: 10px;
    outline: none;
    border-radius: 12px;
    background: #e9e9e9;
  }

  &_no-data {
    margin-top: -4px;
    height: 80px;
    text-align: center;
    padding-top: 45px;
    border: 1px solid var(--border-color);
    background-color: #fafafa;
    color: #88b2d0;
    font-size: 18px;
  }

  &_select-all-rows {
    margin-left: 20px;
  }
}
</style>
